import React, { useEffect } from 'react'
import { useCustomerStore } from '../../stores/customerStore'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const {
    customers,
    selectedCustomer,
    setSelectedCustomer,
    loadCustomers,
    isLoading,
    isSidebarCollapsed,
    setSidebarCollapsed
  } = useCustomerStore()

  useEffect(() => {
    loadCustomers()
  }, [])

  const handleCustomerClick = (customer: string | 'all') => {
    setSelectedCustomer(customer)
    // 모바일에서 선택 후 사이드바 닫기
    if (window.innerWidth < 1024) {
      onClose()
    }
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
          overflow-y-auto z-30 transition-all duration-300
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isSidebarCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        {/* Header 공간 확보 */}
        <div className="h-[88px] flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-gray-700 dark:to-gray-800">
          {!isSidebarCollapsed && (
            <h3 className="text-sm font-semibold text-primary-800 dark:text-gray-200 uppercase tracking-wide">
              고객사 선택
            </h3>
          )}
          {/* 토글 버튼 */}
          <button
            onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
            className="p-2 hover:bg-primary-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            title={isSidebarCollapsed ? '메뉴 펼치기' : '메뉴 접기'}
          >
            <svg
              className={`w-5 h-5 text-primary-700 dark:text-gray-300 transition-transform ${isSidebarCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-500 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">로딩 중...</p>
            </div>
          ) : (
            <>
              {/* 전체 고객사 버튼 */}
              <button
                className={`
                  w-full text-left rounded-lg mb-2 font-medium transition-colors
                  ${isSidebarCollapsed ? 'px-2 py-2' : 'px-4 py-3'}
                  ${
                    selectedCustomer === 'all'
                      ? 'bg-primary-600 dark:bg-gray-700 text-white shadow-md dark:ring-1 dark:ring-white/20'
                      : 'bg-gray-100 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/70'
                  }
                `}
                onClick={() => handleCustomerClick('all')}
                aria-current={selectedCustomer === 'all' ? 'page' : undefined}
                title={isSidebarCollapsed ? '전체 고객사' : ''}
              >
                <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                  <svg
                    className={`w-5 h-5 ${isSidebarCollapsed ? '' : 'mr-2'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  {!isSidebarCollapsed && '전체 고객사'}
                </div>
              </button>

              {/* 구분선 */}
              {!isSidebarCollapsed && <div className="border-t border-gray-200 dark:border-gray-600 my-3"></div>}

              {/* 고객 목록 */}
              <div className="space-y-1">
                {customers.map((customer) => (
                  <button
                    key={customer}
                    className={`
                      w-full text-left rounded-lg font-medium transition-colors
                      ${isSidebarCollapsed ? 'px-2 py-2' : 'px-4 py-3'}
                      ${
                        selectedCustomer === customer
                          ? 'bg-primary-600 dark:bg-gray-700 text-white shadow-md dark:ring-1 dark:ring-white/20'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70'
                      }
                    `}
                    onClick={() => handleCustomerClick(customer)}
                    aria-current={selectedCustomer === customer ? 'page' : undefined}
                    title={isSidebarCollapsed ? customer : ''}
                  >
                    <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                      <svg
                        className={`w-5 h-5 ${isSidebarCollapsed ? '' : 'mr-2'} flex-shrink-0`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      {!isSidebarCollapsed && (
                        <span className="truncate">{customer}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
    </>
  )
}
